// The pricing UI itself — region selector, monthly/yearly switch, plan cards
// and the disclaimer. Shared by the public /pricing page and the signed-in
// /plans page so the two cannot describe the product differently.
//
// Every value comes from `pricing_catalog()` (ADR-0009, ADR-0010). Nothing about
// a tier, a limit or a price is written down here.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";
import {
  formatLimit, formatMoney, priceFor, yearlySavingPercent,
} from "../lib/entitlements";
import type { BillingInterval, PricingCatalog } from "../lib/types";

/** What a plan grants for a feature, falling back to the feature's own default. */
export function grantOf(catalog: PricingCatalog, planCode: string, featureKey: string) {
  const row = catalog.entitlements.find(
    (e) => e.plan_code === planCode && e.feature_key === featureKey,
  );
  const feature = catalog.features.find((f) => f.key === featureKey);
  return {
    enabled: row?.enabled ?? false,
    limit: row ? row.limit_value : null,
    kind: feature?.kind ?? "boolean",
    unit: feature?.unit ?? null,
  };
}

/** One cell of the comparison table. */
export function featureCell(
  catalog: PricingCatalog,
  planCode: string,
  featureKey: string,
): string {
  const g = grantOf(catalog, planCode, featureKey);
  if (!g.enabled) return "—";
  if (g.kind === "boolean") return "Included";
  return formatLimit(g.limit === null ? null : Number(g.limit), g.unit);
}

/** The lines under a price. Chosen for what people actually compare on. */
const HEADLINE_FEATURES = [
  "connections", "posts_per_month", "video_publishing", "scheduling_horizon_days",
];

function headline(catalog: PricingCatalog, planCode: string): string[] {
  return HEADLINE_FEATURES.map((key) => {
    const feature = catalog.features.find((f) => f.key === key);
    if (!feature) return "";
    const g = grantOf(catalog, planCode, key);
    if (g.kind === "boolean") return g.enabled ? feature.name : `No ${feature.name.toLowerCase()}`;
    if (!g.enabled || (g.limit !== null && Number(g.limit) === 0)) {
      return `No ${feature.name.toLowerCase()}`;
    }
    return `${formatLimit(g.limit === null ? null : Number(g.limit), feature.unit)} · ${feature.name.toLowerCase()}`;
  }).filter(Boolean);
}

export interface PricingTableProps {
  catalog: PricingCatalog;
  region: string;
  onRegion: (code: string) => void;
  yearly: boolean;
  onYearly: (v: boolean) => void;
  /** Highlighted as "your plan" when it matches. */
  currentPlan?: string | null;
  /** Absent on the public page: signing in comes first. */
  onChoose?: (planCode: string, interval: BillingInterval) => void;
  chooseLabel?: string;
  busyPlan?: string | null;
  /** Shown in place of the button, e.g. "a request is already being reviewed". */
  disabledReason?: string | null;
}

export function PricingTable({
  catalog, region, onRegion, yearly, onYearly,
  currentPlan, onChoose, chooseLabel = "Choose", busyPlan, disabledReason,
}: PricingTableProps) {
  const regionRow = catalog.regions.find((r) => r.code === region) ?? null;

  // Shown next to the switch: the best saving actually on offer, so the label
  // is a fact rather than a claim someone has to keep in sync.
  const bestSaving = useMemo(() => {
    const all = catalog.plans
      .map((p) => yearlySavingPercent(catalog, p.code, region))
      .filter((n): n is number => n !== null);
    return all.length ? Math.max(...all) : null;
  }, [catalog, region]);

  const suffix: Record<BillingInterval, string> = {
    month: "/month", year: "/year", lifetime: "one-time",
  };

  return (
    <div className="pricing-showcase">
      <style>{PRICING_SHOWCASE_STYLES}</style>

      <div className="pricing-controls">
        {/* Region: a guess from the browser, corrected here, settled at checkout. */}
        <div className="segmented" role="group" aria-label="Pricing region">
          {catalog.regions.map((r) => (
            <button
              key={r.code}
              type="button"
              className={r.code === region ? "on" : ""}
              aria-pressed={r.code === region}
              onClick={() => onRegion(r.code)}
            >
              {r.name}
            </button>
          ))}
        </div>

        <div className="billing-toggle" role="group" aria-label="Billing interval">
          <button
            type="button"
            className={!yearly ? "on" : ""}
            aria-pressed={!yearly}
            onClick={() => onYearly(false)}
          >
            Monthly
          </button>
          <button
            type="button"
            className={yearly ? "on" : ""}
            aria-pressed={yearly}
            onClick={() => onYearly(true)}
          >
            Yearly
            {bestSaving !== null && <span className="save">Save up to {bestSaving}%</span>}
          </button>
        </div>
      </div>

      <div className="plan-grid">
        {catalog.plans.map((plan) => {
          // A one-off plan has no renewal to make yearly.
          const interval: BillingInterval =
            plan.billing_period === "lifetime" ? "lifetime" : yearly ? "year" : "month";
          const price = priceFor(catalog, plan.code, region, interval);
          const isCurrent = plan.code === currentPlan;
          const saving = yearly ? yearlySavingPercent(catalog, plan.code, region) : null;
          const featured = plan.code === "pro";
          const free = price?.amount_minor === 0;

          return (
            <section
              key={plan.code}
              className={`plan-card${featured ? " featured" : ""}${isCurrent ? " current" : ""}`}
            >
              {featured && <span className="ribbon">Most popular</span>}
              {isCurrent && <span className="chip ok current-chip">Your plan</span>}

              <div className="plan-heading">
                <span className="plan-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 3 4.5 7v5c0 4.6 3.2 7.6 7.5 9 4.3-1.4 7.5-4.4 7.5-9V7L12 3Z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </span>
                <h2>{plan.name}</h2>
              </div>
              {plan.tagline && <p className="muted tagline">{plan.tagline}</p>}

              <p className="amount">
                {price
                  ? free
                    ? <span className="figure">Free</span>
                    : <>
                        <span className="figure">{formatMoney(price.amount_minor, price.currency)}</span>
                        <span className="per">{suffix[interval]}</span>
                      </>
                  : <span className="muted">Price on request</span>}
              </p>

              {saving !== null && <p className="saving">Save {saving}% versus monthly</p>}
              {interval === "year" && price && price.amount_minor > 0 && (
                <p className="muted equivalent">
                  {formatMoney(Math.round(price.amount_minor / 12), price.currency)}/month, billed yearly
                </p>
              )}

              <ul className="plan-features">
                {headline(catalog, plan.code).map((line) => (
                  <li key={line}><span aria-hidden="true">✓</span>{line}</li>
                ))}
              </ul>

              {onChoose ? (
                <button
                  className={`${featured && !isCurrent ? "primary" : ""}${isCurrent ? " current-plan-action" : ""}`}
                  disabled={isCurrent || free || !!busyPlan || !!disabledReason}
                  onClick={() => onChoose(plan.code, interval)}
                >
                  {busyPlan === plan.code ? "Sending…"
                    : isCurrent ? "Current plan"
                    : free ? "Included by default"
                    : chooseLabel}
                </button>
              ) : (
                <a className="btn-link primary plan-cta" href="/signin">
                  {isCurrent ? "Current plan" : free ? "Start free" : "Get started"}
                </a>
              )}
            </section>
          );
        })}
      </div>

      {disabledReason && <p className="notice upgrade plan-blocked">{disabledReason}</p>}

      <div className="pricing-assurance">
        <span className="assurance-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 3 4.5 7v5c0 4.6 3.2 7.6 7.5 9 4.3-1.4 7.5-4.4 7.5-9V7L12 3Z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        </span>
        <div>
          <strong>Prices shown are indicative.</strong>
          <PricingDisclaimer regionName={regionRow?.name} currency={regionRow?.currency} />
        </div>
      </div>
    </div>
  );
}

const PRICING_SHOWCASE_STYLES = `
  /* Themed like the rest of the app (theme.css): a light and a dark value for
     every surface, and a foreground for every fill. Before this the block was
     forty literal colours with no dark half, so /pricing and /plans were the
     two screens that stayed white when everything around them went dark.

     --ps-accent is for text and borders and goes pale in dark mode.
     --ps-solid-* is for filled backgrounds and stays saturated, which is why
     --ps-on-solid is white in both themes and --ps-accent is never used as a
     fill behind white text. */
  .pricing-showcase {
    --ps-accent: #2458eb;
    --ps-violet: #7c2cff;
    --ps-pink: #e50078;
    --ps-solid-from: #335cf0;
    --ps-solid-to: #1646df;
    --ps-on-solid: #ffffff;

    --ps-card: rgba(255, 255, 255, .92);
    --ps-card-solid: #ffffff;
    --ps-sunken: #f7f8fb;
    --ps-line: #dce1ed;
    --ps-line-soft: #edf0f5;
    --ps-ink-soft: #505a73;
    --ps-tint: #eef2ff;
    --ps-tint-2: #f5f8ff;
    --ps-tint-strong: #dfe7ff;
    --ps-violet-tint: #f2eaff;
    --ps-pink-tint: #fff0f7;
    --ps-ring: #b9c8ff;
    --ps-ring-soft: rgba(36, 88, 235, .15);

    --ps-halo-a: rgba(36, 88, 235, .08);
    --ps-halo-b: rgba(124, 44, 255, .07);
    --ps-shadow-soft: 0 8px 24px rgba(23, 37, 84, .05);
    --ps-shadow-card: 0 14px 38px rgba(33, 45, 88, .055);
    --ps-shadow-lift: 0 16px 42px rgba(36, 88, 235, .14);
    --ps-shadow-toggle: 0 3px 12px rgba(23, 37, 84, .08);
    --ps-glow: 0 7px 18px rgba(36, 88, 235, .22);

    position: relative;
    margin: 2rem 0 3rem;
    padding-left: .5rem;
    isolation: isolate;
    box-sizing: border-box;
  }
  .pricing-showcase::before {
    content: "";
    position: absolute;
    z-index: -1;
    inset: 4.5rem -8vw auto;
    height: 34rem;
    pointer-events: none;
    background:
      radial-gradient(circle at 12% 45%, var(--ps-halo-a), transparent 28%),
      radial-gradient(circle at 88% 30%, var(--ps-halo-b), transparent 27%);
  }
  .pricing-showcase .pricing-controls {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 1rem;
    margin: 0 0 2rem;
  }
  .pricing-showcase .pricing-controls > .segmented { justify-self: start; }
  .pricing-showcase .segmented {
    border: 1px solid var(--ps-line);
    background: var(--ps-card);
    box-shadow: var(--ps-shadow-soft);
  }
  .pricing-showcase .segmented button { min-height: 2.7rem; }
  .pricing-showcase .segmented button.on {
    background: var(--ps-tint);
    color: var(--ps-accent);
    box-shadow: inset 0 0 0 1px var(--ps-ring-soft);
  }
  .pricing-showcase .billing-toggle {
    grid-column: 2;
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: min(100%, 25rem);
    padding: .25rem;
    border: 1px solid var(--ps-line);
    border-radius: .75rem;
    background: var(--ps-sunken);
  }
  .pricing-showcase .billing-toggle button {
    min-height: 2.65rem;
    padding: .45rem 1.15rem;
    border: 0;
    border-radius: .55rem;
    background: transparent;
    color: var(--ps-ink-soft);
    box-shadow: none;
    white-space: nowrap;
  }
  .pricing-showcase .billing-toggle button.on {
    background: var(--ps-card-solid);
    color: var(--ps-accent);
    box-shadow: var(--ps-shadow-toggle), inset 0 0 0 1px var(--ps-ring);
  }
  .pricing-showcase .billing-toggle .save {
    display: inline-block;
    margin-left: .4rem;
    padding: .14rem .4rem;
    border-radius: 999px;
    background: var(--ps-tint-strong);
    color: var(--ps-accent);
    font-size: .65rem;
    font-weight: 700;
  }
  .pricing-showcase .plan-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13.5rem, 1fr));
    gap: 1rem;
    align-items: stretch;
  }
  .pricing-showcase .plan-card {
    min-width: 0;
    padding: 1.6rem 1.5rem 1.45rem;
    border: 1px solid var(--ps-line);
    border-radius: 1rem;
    background: var(--ps-card);
    box-shadow: var(--ps-shadow-card);
    overflow: visible;
  }
  .pricing-showcase .plan-card.featured {
    border-color: var(--ps-accent);
    background: linear-gradient(180deg, var(--ps-tint-2) 0%, var(--ps-card-solid) 35%);
    box-shadow: var(--ps-shadow-lift), inset 0 0 0 1px var(--ps-ring-soft);
    transform: translateY(-.55rem);
  }
  .pricing-showcase .plan-card.current {
    background: linear-gradient(180deg, var(--ps-tint-2), var(--ps-card-solid));
  }
  .pricing-showcase .plan-heading { display: flex; align-items: center; gap: .6rem; }
  .pricing-showcase .plan-heading h2 {
    margin: 0;
    font-size: 1.25rem;
    letter-spacing: -.02em;
  }
  .pricing-showcase .plan-icon {
    display: grid;
    width: 2rem;
    height: 2rem;
    place-items: center;
    border-radius: .65rem;
    background: var(--ps-tint);
    color: var(--ps-accent);
  }
  .pricing-showcase .plan-icon svg,
  .pricing-showcase .assurance-icon svg { width: 1.15rem; stroke: currentColor; stroke-width: 1.8; }
  .pricing-showcase .plan-card:nth-child(4n + 2) .plan-icon { background: var(--ps-violet-tint); color: var(--ps-violet); }
  .pricing-showcase .plan-card:nth-child(4n + 4) .plan-icon { background: var(--ps-pink-tint); color: var(--ps-pink); }
  .pricing-showcase .plan-card .tagline { margin: .7rem 0 1.25rem; min-height: 2.6em; line-height: 1.5; }
  .pricing-showcase .plan-card .amount { min-height: 2.9rem; margin-bottom: .15rem; }
  .pricing-showcase .plan-card .figure { font-size: 2.15rem; letter-spacing: -.045em; }
  .pricing-showcase .plan-card .saving { color: var(--ps-accent); }
  .pricing-showcase .plan-card .ribbon {
    top: -.68rem;
    right: 1.15rem;
    padding: .32rem .9rem;
    border-radius: .42rem;
    background: linear-gradient(135deg, var(--ps-solid-from), var(--ps-solid-to));
    color: var(--ps-on-solid);
    box-shadow: var(--ps-glow);
  }
  .pricing-showcase .plan-features {
    display: grid;
    gap: .72rem;
    min-height: 11.25rem;
    margin: 1.35rem 0;
    padding: 1.1rem 0 0;
    border-top: 1px solid var(--ps-line-soft);
    list-style: none;
  }
  .pricing-showcase .plan-features li {
    display: grid;
    grid-template-columns: 1.15rem 1fr;
    gap: .45rem;
    align-items: start;
    font-size: .86rem;
    line-height: 1.4;
  }
  .pricing-showcase .plan-features li > span {
    display: grid;
    width: 1.05rem;
    height: 1.05rem;
    place-items: center;
    margin-top: .05rem;
    border: 1.5px solid currentColor;
    border-radius: 50%;
    color: var(--ps-accent);
    font-size: .65rem;
    font-weight: 800;
  }
  .pricing-showcase .plan-card .plan-cta,
  .pricing-showcase .plan-card button {
    min-height: 2.85rem;
    border-radius: .55rem;
    font-weight: 700;
  }
  .pricing-showcase .plan-card:not(.featured) .plan-cta.primary {
    border: 1px solid var(--ps-accent);
    background: var(--ps-card-solid);
    color: var(--ps-accent);
  }
  .pricing-showcase .plan-card.featured .plan-cta.primary,
  .pricing-showcase .plan-card.featured button.primary {
    background: linear-gradient(135deg, var(--ps-solid-from), var(--ps-solid-to));
    color: var(--ps-on-solid);
    box-shadow: var(--ps-glow);
  }
  .pricing-showcase .plan-card button.current-plan-action {
    border-color: var(--ps-line);
    background: var(--ps-sunken);
    color: var(--ps-ink-soft);
    opacity: 1;
    cursor: default;
  }
  .pricing-showcase .pricing-assurance {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    align-items: start;
    margin-top: 1rem;
    padding: 1.15rem 1.35rem;
    border: 1px solid var(--ps-line);
    border-radius: 1rem;
    background: linear-gradient(100deg, var(--ps-tint-2), var(--ps-card));
  }
  .pricing-showcase .assurance-icon {
    display: grid;
    width: 2.55rem;
    height: 2.55rem;
    place-items: center;
    border-radius: .8rem;
    background: var(--ps-solid-from);
    color: var(--ps-on-solid);
    box-shadow: var(--ps-glow);
  }
  .pricing-showcase .pricing-assurance > div > strong { display: block; margin: .05rem 0 .25rem; }
  .pricing-showcase .pricing-disclaimer { margin: 0; padding: 0; border: 0; background: transparent; }
  .pricing-showcase .pricing-disclaimer > strong:first-child { display: none; }
  @media (max-width: 760px) {
    .pricing-showcase { padding-left: .25rem; }
    .pricing-showcase .pricing-controls { display: flex; flex-direction: column; align-items: stretch; }
    .pricing-showcase .pricing-controls > .segmented { align-self: flex-start; }
    .pricing-showcase .billing-toggle { width: 100%; }
    .pricing-showcase .plan-card.featured { transform: none; }
    .pricing-showcase .plan-card .tagline,
    .pricing-showcase .plan-features { min-height: 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    .pricing-showcase .plan-card.featured { transform: none; }
  }

  /* Same mapping twice, so a forced theme beats the operating system in both
     directions — see theme.css. */
  @media (prefers-color-scheme: dark) {
    :root:not([data-theme="light"]) .pricing-showcase {
    --ps-accent: #7fa0ff;
    --ps-violet: #b98bff;
    --ps-pink: #ff77b6;
    --ps-solid-from: #3d6ef5;
    --ps-solid-to: #2456e8;

    --ps-card: color-mix(in srgb, var(--panel) 92%, transparent);
    --ps-card-solid: var(--panel);
    --ps-sunken: var(--bg);
    --ps-line: var(--line);
    --ps-line-soft: color-mix(in srgb, var(--line) 65%, transparent);
    --ps-ink-soft: var(--muted);
    --ps-tint: color-mix(in srgb, var(--ps-accent) 15%, var(--panel));
    --ps-tint-2: color-mix(in srgb, var(--ps-accent) 8%, var(--panel));
    --ps-tint-strong: color-mix(in srgb, var(--ps-accent) 24%, var(--panel));
    --ps-violet-tint: color-mix(in srgb, var(--ps-violet) 18%, var(--panel));
    --ps-pink-tint: color-mix(in srgb, var(--ps-pink) 18%, var(--panel));
    --ps-ring: color-mix(in srgb, var(--ps-accent) 45%, transparent);
    --ps-ring-soft: color-mix(in srgb, var(--ps-accent) 28%, transparent);

    --ps-halo-a: rgba(61, 110, 245, .16);
    --ps-halo-b: rgba(150, 90, 255, .13);
    --ps-shadow-soft: 0 8px 24px rgb(0 0 0 / .35);
    --ps-shadow-card: 0 14px 38px rgb(0 0 0 / .4);
    --ps-shadow-lift: 0 16px 42px rgb(0 0 0 / .5);
    --ps-shadow-toggle: 0 3px 12px rgb(0 0 0 / .4);
    --ps-glow: 0 7px 18px rgb(36 88 235 / .45);
    }
  }
  :root[data-theme="dark"] .pricing-showcase {
    --ps-accent: #7fa0ff;
    --ps-violet: #b98bff;
    --ps-pink: #ff77b6;
    --ps-solid-from: #3d6ef5;
    --ps-solid-to: #2456e8;

    --ps-card: color-mix(in srgb, var(--panel) 92%, transparent);
    --ps-card-solid: var(--panel);
    --ps-sunken: var(--bg);
    --ps-line: var(--line);
    --ps-line-soft: color-mix(in srgb, var(--line) 65%, transparent);
    --ps-ink-soft: var(--muted);
    --ps-tint: color-mix(in srgb, var(--ps-accent) 15%, var(--panel));
    --ps-tint-2: color-mix(in srgb, var(--ps-accent) 8%, var(--panel));
    --ps-tint-strong: color-mix(in srgb, var(--ps-accent) 24%, var(--panel));
    --ps-violet-tint: color-mix(in srgb, var(--ps-violet) 18%, var(--panel));
    --ps-pink-tint: color-mix(in srgb, var(--ps-pink) 18%, var(--panel));
    --ps-ring: color-mix(in srgb, var(--ps-accent) 45%, transparent);
    --ps-ring-soft: color-mix(in srgb, var(--ps-accent) 28%, transparent);

    --ps-halo-a: rgba(61, 110, 245, .16);
    --ps-halo-b: rgba(150, 90, 255, .13);
    --ps-shadow-soft: 0 8px 24px rgb(0 0 0 / .35);
    --ps-shadow-card: 0 14px 38px rgb(0 0 0 / .4);
    --ps-shadow-lift: 0 16px 42px rgb(0 0 0 / .5);
    --ps-shadow-toggle: 0 3px 12px rgb(0 0 0 / .4);
    --ps-glow: 0 7px 18px rgb(36 88 235 / .45);
  }
`;

/**
 * Exported on its own because the pre-rendered pricing page must carry it even
 * before any price has loaded — it is the statement that makes an indicative
 * price defensible, not decoration around one (ADR-0010).
 */
export function PricingDisclaimer({
  regionName, currency,
}: { regionName?: string; currency?: string }) {
  return (
    <p className="pricing-disclaimer">
      <strong>Prices shown are indicative.</strong> The amount you are actually charged is
      determined by the billing address on your account, the location you are accessing the
      service from, and the payment processor you choose at checkout. Local taxes — GST, VAT,
      sales tax — may be added on top, and currency conversion or cross-border fees may be
      applied by your bank or card issuer.
      {regionName && (
        <> You are seeing <strong>{regionName}</strong> pricing in {currency}, selected from your
        browser's language and time zone — change the region above if that is not where you are
        billed.</>
      )}
    </p>
  );
}

/**
 * What a one-off tier actually promises — the questions a buyer has to have
 * answered *before* paying once, not after.
 *
 * Every claim here is either checkable in this repo or already written into the
 * Terms, because this page is read by App Review and by people deciding to hand
 * over a single non-recurring payment. Two of them carry weight:
 *
 *  - "Lifetime" is the lifetime of the service, not of the buyer. It is the one
 *    disclosure every buyer's guide to these deals leads with, and the one
 *    omission that turns a happy customer into a chargeback.
 *  - Storage is not part of the bargain. A one-time payment against bytes that
 *    bill every month is the structural mismatch that kills one-off tiers; the
 *    workspace library is a staging area and the originals stay with the
 *    customer. Reasoning in migration 20260823000800.
 *
 * Driven by `billing_period`, like everything else: no one-off tier on offer,
 * no card. Renders standalone (no catalogue) for the pre-rendered page.
 */
export function LifetimeExplainer({ catalog }: { catalog?: PricingCatalog }) {
  const plan = catalog?.plans.find((p) => p.billing_period === "lifetime");
  if (catalog && !plan) return null;

  const name = plan?.name ?? "Lifetime";
  const storage = catalog && plan
    ? featureCell(catalog, plan.code, "cloud_storage_bytes")
    : null;

  return (
    <div className="card lifetime-card">
      <h2>How {name} works</h2>
      <p className="muted lifetime-lede">
        One payment, no renewal, no card kept on file. Worth reading in full before you buy —
        a one-off plan is a different bargain from a subscription, and these are its terms.
      </p>

      <dl className="lifetime-points">
        <dt>“{name}” means the lifetime of the service, not yours</dt>
        <dd>
          It is access for as long as {COMPANY.product} operates —
          the same thing every one-off software deal means, said plainly rather than left for
          you to discover. We are not planning to stop, and if we ever have to,{" "}
          <Link to="/terms#availability">our Terms</Link> commit us to reasonable notice so you
          can get your work out.
        </dd>

        <dt>You bring your own storage</dt>
        <dd>
          {name} is a publishing tool, not a media locker. Your originals stay where they already
          live — on your phone, your computer, or your own Google Drive or Dropbox — and are
          pulled in when you publish. The workspace library
          {storage ? <> is a working area of <strong>{storage}</strong>,</> : <> is a working area,</>}
          {" "}sized for what is in flight rather than for an archive.
        </dd>
        <dd className="muted why">
          Why: everything else a plan grants costs what it cost the day it was built. Bytes at
          rest bill again every month, forever, against a payment that happened once. Tiers that
          promise unlimited storage for a single payment are the ones that quietly disappear —
          so we priced the part that keeps costing money out of the deal instead of pricing the
          deal to fail.
        </dd>

        <dt>Everything else is unmetered</dt>
        <dd>
          Unlimited connected accounts, unlimited posts, scheduling with no horizon, and video up
          to twelve hours. No per-seat pricing and no post allowance to watch. The full grid is
          in the feature comparison.
        </dd>

        <dt>Improvements are included; separate products are not</dt>
        <dd>
          Every network we add and every improvement to publishing, scheduling and media handling
          reaches your plan at no extra cost. If we ever build something that carries its own
          running cost per use — anything metered by a third party — it will be offered
          separately and priced honestly, never bolted on as a surprise.
        </dd>

        <dt>One workspace, and it stays with you</dt>
        <dd>
          A {name.toLowerCase()} plan belongs to the workspace it was bought for and to the person
          who bought it. It is not transferable or resellable. Everyone you invite into that
          workspace publishes under it.
        </dd>

        <dt>Paying for it</dt>
        <dd>
          There is no automatic checkout yet: choosing {name} sends us a request and we reply with
          how to pay in your region. Nothing is charged until you have that message and act on
          it. Fees are non-refundable once paid except where the law says otherwise —{" "}
          <Link to="/terms#fees">Terms, section 12</Link>.
        </dd>
      </dl>
    </div>
  );
}
